import { Head } from "vite-react-ssg";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";

const UPDATED = "10 août 2026";
const CONTACT = "gregory.scafarto@gmail.com";

const Confidentialite = () => {
  return (
    <>
      <Head>
        <title>Politique de confidentialité — CoachOS</title>
        <meta
          name="description"
          content="Politique de confidentialité de CoachOS : données collectées, données de santé (Apple Santé / Health Connect), finalités, sous-traitants, conservation et droits RGPD."
        />
        <link rel="canonical" href="https://www.coach-os.eu/confidentialite" />
      </Head>

      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="container max-w-3xl py-24">
          <h1 className="mb-2 text-3xl font-bold text-foreground">Politique de confidentialité</h1>
          <p className="mb-10 text-sm text-muted-foreground">Dernière mise à jour : {UPDATED}</p>

          <p className="mb-10 leading-relaxed text-muted-foreground">
            La présente politique explique quelles données personnelles sont collectées par CoachOS
            (site web et applications mobiles iOS et Android), pourquoi, sur quelle base légale, avec
            qui elles sont partagées, combien de temps elles sont conservées et quels sont vos droits.
            Elle couvre notamment le traitement des <strong className="text-foreground">données de
            santé et de forme physique</strong>.
          </p>

          {/* 1 */}
          <section className="mb-8">
            <h2 className="mb-3 text-xl font-semibold text-foreground">1. Responsable du traitement</h2>
            <p className="leading-relaxed text-muted-foreground">
              Gregory Scafarto — CoachOS<br />
              12B Rue Sainte Isaure, 75018 Paris, France<br />
              Contact : <a href={`mailto:${CONTACT}`} className="text-accent hover:underline">{CONTACT}</a>
            </p>
          </section>

          {/* 2 */}
          <section className="mb-8">
            <h2 className="mb-3 text-xl font-semibold text-foreground">2. Données que nous collectons</h2>
            <ul className="list-disc space-y-2 pl-5 leading-relaxed text-muted-foreground">
              <li><strong className="text-foreground">Données de compte :</strong> nom, prénom, adresse email, mot de passe (chiffré), rôle (coach ou client).</li>
              <li><strong className="text-foreground">Données de coaching :</strong> programmes d'entraînement, plans nutritionnels, séances, mesures et suivi, photos de progression, notes et messages échangés entre coach et client.</li>
              <li><strong className="text-foreground">Données de santé et de forme physique :</strong> voir la section 4 dédiée.</li>
              <li><strong className="text-foreground">Données de paiement :</strong> gérées par notre prestataire Stripe ; nous ne stockons pas vos numéros de carte.</li>
              <li><strong className="text-foreground">Données techniques et d'usage :</strong> journaux, données de diagnostic et d'erreur, statistiques d'utilisation, type d'appareil, pour assurer le fonctionnement et améliorer le service.</li>
            </ul>
          </section>

          {/* 3 */}
          <section className="mb-8">
            <h2 className="mb-3 text-xl font-semibold text-foreground">3. Finalités et bases légales</h2>
            <ul className="list-disc space-y-2 pl-5 leading-relaxed text-muted-foreground">
              <li>Fournir le service de coaching (création de compte, programmes, suivi) — <em>exécution du contrat</em>.</li>
              <li>Permettre le suivi de la progression et le partage avec votre coach — <em>exécution du contrat</em> et, pour les données de santé, <em>votre consentement</em>.</li>
              <li>Sécurité, prévention des fraudes et bon fonctionnement technique — <em>intérêt légitime</em>.</li>
              <li>Mesure d'audience et amélioration du produit — <em>intérêt légitime</em> ou <em>consentement</em> selon les cas.</li>
              <li>Respect de nos obligations légales (facturation, comptabilité) — <em>obligation légale</em>.</li>
            </ul>
          </section>

          {/* 4 — SANTÉ */}
          <section className="mb-8 rounded-2xl border border-[#e7e9f1] bg-[#F7FAFF] p-6">
            <h2 className="mb-3 text-xl font-semibold text-foreground">4. Données de santé et de forme physique</h2>
            <p className="mb-4 leading-relaxed text-muted-foreground">
              Avec votre <strong className="text-foreground">consentement explicite</strong>, l'application CoachOS accède
              <strong className="text-foreground"> en lecture seule</strong> à certaines données de santé et d'activité
              provenant d'<strong className="text-foreground">Apple Santé (HealthKit)</strong> sur iOS et de
              <strong className="text-foreground"> Health Connect</strong> sur Android.
            </p>
            <p className="mb-2 font-semibold text-foreground">Données concernées :</p>
            <ul className="mb-4 list-disc space-y-1 pl-5 leading-relaxed text-muted-foreground">
              <li>Nombre de pas</li>
              <li>Données de sommeil (durée)</li>
              <li>Poids corporel</li>
              <li>Fréquence cardiaque au repos</li>
              <li>Variabilité de la fréquence cardiaque (VFC / HRV)</li>
            </ul>
            <ul className="list-disc space-y-2 pl-5 leading-relaxed text-muted-foreground">
              <li><strong className="text-foreground">Finalités :</strong> afficher votre progression dans l'application et la partager avec le coach que vous avez choisi, afin de personnaliser votre accompagnement sportif.</li>
              <li><strong className="text-foreground">Base légale :</strong> votre consentement (art. 6.1.a et art. 9.2.a du RGPD pour les données de santé), donné via les autorisations système d'Apple Santé / Health Connect, et révocable à tout moment dans les réglages de votre téléphone.</li>
              <li><strong className="text-foreground">Collecte & stockage :</strong> l'application lit uniquement les jours récents nécessaires au suivi et les enregistre de manière sécurisée sur notre infrastructure Supabase, hébergée dans l'Union européenne. CoachOS <strong className="text-foreground">n'écrit aucune donnée</strong> dans Apple Santé ou Health Connect.</li>
              <li><strong className="text-foreground">Partage :</strong> ces données ne sont partagées qu'avec votre coach. Elles ne sont <strong className="text-foreground">jamais vendues</strong>, ni utilisées à des fins <strong className="text-foreground">publicitaires</strong>, ni transmises à des tiers à ces fins.</li>
              <li><strong className="text-foreground">Conservation :</strong> conservées pendant l'utilisation du service, supprimées sur demande ou à la suppression de votre compte.</li>
              <li><strong className="text-foreground">Contrôle :</strong> vous pouvez à tout moment révoquer l'accès aux données de santé depuis les réglages iOS / Android, ou demander leur suppression en nous contactant.</li>
            </ul>
          </section>

          {/* 5 */}
          <section className="mb-8">
            <h2 className="mb-3 text-xl font-semibold text-foreground">5. Partage et sous-traitants</h2>
            <p className="mb-3 leading-relaxed text-muted-foreground">
              Vos données ne sont partagées qu'avec le coach que vous avez choisi et avec les prestataires
              techniques suivants, agissant comme sous-traitants pour notre compte :
            </p>
            <ul className="list-disc space-y-2 pl-5 leading-relaxed text-muted-foreground">
              <li><strong className="text-foreground">Supabase</strong> — base de données et authentification (hébergement Union européenne).</li>
              <li><strong className="text-foreground">Vercel</strong> — hébergement du site et diffusion.</li>
              <li><strong className="text-foreground">Stripe</strong> — traitement des paiements.</li>
              <li><strong className="text-foreground">Sentry</strong> — détection des erreurs techniques.</li>
              <li><strong className="text-foreground">PostHog</strong> — mesure d'audience et statistiques d'usage.</li>
            </ul>
            <p className="mt-3 leading-relaxed text-muted-foreground">
              Nous ne vendons jamais vos données personnelles.
            </p>
          </section>

          {/* 6 */}
          <section className="mb-8">
            <h2 className="mb-3 text-xl font-semibold text-foreground">6. Transferts hors de l'Union européenne</h2>
            <p className="leading-relaxed text-muted-foreground">
              Vos données de compte, de coaching et de santé sont hébergées dans l'Union européenne (Supabase).
              Certains prestataires peuvent traiter des données techniques hors UE ; dans ce cas, ces transferts
              sont encadrés par des garanties appropriées (clauses contractuelles types de la Commission européenne).
            </p>
          </section>

          {/* 7 */}
          <section className="mb-8">
            <h2 className="mb-3 text-xl font-semibold text-foreground">7. Durée de conservation</h2>
            <p className="leading-relaxed text-muted-foreground">
              Vos données sont conservées le temps de votre utilisation du service. À la suppression de votre
              compte, elles sont effacées ou anonymisées, sauf obligation légale de conservation (par exemple
              les données de facturation).
            </p>
          </section>

          {/* 8 */}
          <section className="mb-8">
            <h2 className="mb-3 text-xl font-semibold text-foreground">8. Sécurité</h2>
            <p className="leading-relaxed text-muted-foreground">
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées (chiffrement en
              transit, contrôle d'accès, hébergement sécurisé) pour protéger vos données contre tout accès,
              modification ou divulgation non autorisés.
            </p>
          </section>

          {/* 9 */}
          <section className="mb-8">
            <h2 className="mb-3 text-xl font-semibold text-foreground">9. Vos droits</h2>
            <p className="mb-3 leading-relaxed text-muted-foreground">
              Conformément au RGPD, vous disposez des droits d'accès, de rectification, d'effacement, de
              limitation, d'opposition et de portabilité, ainsi que du droit de retirer votre consentement à
              tout moment. Pour les exercer, écrivez-nous à{" "}
              <a href={`mailto:${CONTACT}`} className="text-accent hover:underline">{CONTACT}</a>.
            </p>
            <p className="leading-relaxed text-muted-foreground">
              Vous pouvez supprimer votre compte et vos données à tout moment depuis la page{" "}
              <a href="/suppression-compte" className="text-accent hover:underline">Suppression de compte</a>.
              Vous avez également le droit d'introduire une réclamation auprès de la CNIL (
              <a href="https://www.cnil.fr" className="text-accent hover:underline" target="_blank" rel="noopener noreferrer">cnil.fr</a>).
            </p>
          </section>

          {/* 10 */}
          <section className="mb-8">
            <h2 className="mb-3 text-xl font-semibold text-foreground">10. Cookies</h2>
            <p className="leading-relaxed text-muted-foreground">
              Notre site utilise des cookies. Pour en savoir plus, consultez notre{" "}
              <a
                href="https://www.iubenda.com/privacy-policy/64553210/cookie-policy"
                className="text-accent hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Politique relative aux cookies
              </a>.
            </p>
          </section>

          {/* 11 */}
          <section className="mb-8">
            <h2 className="mb-3 text-xl font-semibold text-foreground">11. Modifications</h2>
            <p className="leading-relaxed text-muted-foreground">
              Nous pouvons mettre à jour la présente politique. Toute modification sera publiée sur cette page,
              avec la date de mise à jour actualisée.
            </p>
          </section>

          {/* 12 */}
          <section>
            <h2 className="mb-3 text-xl font-semibold text-foreground">12. Contact</h2>
            <p className="leading-relaxed text-muted-foreground">
              Pour toute question relative à vos données personnelles :{" "}
              <a href={`mailto:${CONTACT}`} className="text-accent hover:underline">{CONTACT}</a>
            </p>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Confidentialite;
